import React, { useCallback } from 'react';
import CallRow from '../CallRow';
import { CALL_LIST } from '../../support/cypressTags';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { styled } from 'react-free-style';
import LoadMore from '../../components/LoadMore';
import { saveCall } from '../../data/call-todo';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useUpdateCallRecordMutation,
  useDeleteCallRecordMutation,
  CallRecordsDocument,
  CallRecordsDuplicateCheckDocument,
} from '../../generated/graphql';
import prepareUpdateCallRecord from '../../utils/prepare-update-call-record';

const NUMBER_OF_COLUMN = 9;

function Component({
  user,
  desk,
  calls,
  status,
  styles,
  hasMore,
  statuses,
  selectAll,
  onLoadMore,
  onEditDone,
  setToDelete,
  fetchCounts,
  totalCounts,
  editCallTodo,
  selectExcept,
  setShowDeleteDialog,
  setSelectExcept,
  statusOfLoadingMore,
}) {
  const { infiniteLoadingByStatus } = useFlags();
  const [updateCallRecordMutation] = useUpdateCallRecordMutation({
    refetchQueries: [CallRecordsDocument],
  });
  const [deleteCallRecordMutation] = useDeleteCallRecordMutation({
    refetchQueries: [CallRecordsDocument, CallRecordsDuplicateCheckDocument],
  });
  const updateCallRecord = prepareUpdateCallRecord(updateCallRecordMutation, user);

  const loading = !!statusOfLoadingMore?.includes?.(status);
  const statusSettings = statuses?.find?.((_status) => _status.status === status);
  const disabledInfiniteScroll = !infiniteLoadingByStatus || !statusSettings?.infiniteLoading;
  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: () => onLoadMore(status),
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: disabledInfiniteScroll,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 400px 0px',
  });

  const onSelectedChange = useCallback(
    (call) => () => {
      if (selectExcept?.has(call._id)) {
        selectExcept.delete(call._id);
      } else if (selectExcept) {
        selectExcept.add(call._id);
      }
      setSelectExcept(new Set(selectExcept));
    },
    [selectExcept, setSelectExcept]
  );

  const getCallDeskContacts = ({ recipientName, recipient }) => {
    const foundCallAddressBook = desk?.addressBook?.find(({ name }) => {
      if (recipient?.name) {
        return name === recipient?.name;
      }

      return name === recipientName;
    });
    return foundCallAddressBook?.contacts;
  };

  return (
    <tbody data-status={status} data-cy={CALL_LIST.STATUS_SECTION}>
      {calls?.map((call) => (
        <CallRow
          key={`${status}-${call._id}`}
          hideBulkEdits={desk?.settings?.hideBulkEdits}
          desk={desk}
          item={call}
          recipient={call?.recipient}
          editCallTodo={editCallTodo}
          onDeleteItem={(item) => {
            setShowDeleteDialog(true);
            setToDelete(item);
          }}
          statuses={statuses}
          onSave={(callTodo) => {
            const originalCall = call; // Capture original call state
            return saveCall(callTodo, updateCallRecord, deleteCallRecordMutation).then((resp) => {
              const queryResponse = resp.data?.updateCallRecord;
              const updatedCall = queryResponse?.success ? queryResponse?.callRecord : callTodo;

              // Call onEditDone with both updated and original call data
              onEditDone && onEditDone(updatedCall, originalCall);

              return updatedCall;
            });
          }}
          selected={selectAll ? !selectExcept?.has(call._id) : selectExcept?.has(call._id)}
          onSelectedChange={onSelectedChange(call)}
          deskContacts={getCallDeskContacts(call)}
        />
      ))}
      {hasMore && (
        <tr key={`loadMore:${status}`} data-cy={CALL_LIST.LOAD_MORE}>
          <td colSpan={NUMBER_OF_COLUMN}>
            {disabledInfiniteScroll ? (
              <LoadMore
                fetchCounts={fetchCounts[status]}
                totalCounts={totalCounts[status]}
                loading={loading}
                onClick={() => onLoadMore(status)}
              />
            ) : (
              <span
                ref={infiniteRef}
                style={{
                  display: 'flex',
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '10px 0',
                }}
              >
                Loading...
              </span>
            )}
          </td>
        </tr>
      )}
      <tr data-cy={CALL_LIST.STATUS_DIVIDER} key={`statusDivider:${status}`}>
        <td className={styles.divider} colSpan={NUMBER_OF_COLUMN} />
      </tr>
    </tbody>
  );
}

const withStyles = styled({
  divider: {
    height: 20,
  },
});

const CallsStatusGroup = withStyles(Component);

export default CallsStatusGroup;
